import { isEqual, reduce } from "lodash-es";
import type { SyntheticEvent } from "react";
import { toast } from "sonner";
import TurndownService from "turndown";
import configuration from "~/configuration";

import type { TaskEntityData } from "~/components/GlobalNewTaskModal/store/types";

import { FALLBACK_IMAGES } from "../constants/common";
import { TASK_ENTITY_TYPE } from "../constants/objects";
import { routes } from "../constants/routes";

export const getCompanySocialMediaHandleByValue = (companyHandle: string) => {
  return companyHandle.replace("company/", "");
};

export const getPersonSocialMediaHandleFromURL = (profileHandle: string) => {
  // Remove http|https and www from the URL
  const url = profileHandle.replace(/(^\w+:|^)\/\//, "").replace("www.", "");

  // Remove linkedin.com from the URL
  let handle = url.replace("linkedin.com/", "").replace("in/", "");

  // Remove twitter.com from the URL
  handle = handle.replace("twitter.com/", "");

  // Remove facebook.com from the URL
  handle = handle.replace("facebook.com/", "");

  return handle;
};

export const getSocialMediaURLByHandle = (
  handle: string,
  channel: "linkedin" | "twitter",
) => {
  switch (channel) {
    case "linkedin":
      return `https://www.linkedin.com/${handle}`;
    case "twitter":
      return `https://twitter.com/${handle}`;
    default:
      return "";
  }
};

interface ChangedProperty<ValueType> {
  key: string;
  value: ValueType;
}

export const findChangedProperties = <ValueType = unknown>(
  newData: Record<string, ValueType>,
  oldData: Record<string, ValueType>,
): ChangedProperty<ValueType>[] => {
  return reduce(
    newData,
    (result, value, key) => {
      if (!isEqual(oldData[key], value)) {
        result.push({ key, value });
      }
      return result;
    },
    [] as ChangedProperty<ValueType>[],
  );
};

export const copyToClipboard = (
  content: string,
  copyMessage: string | undefined = "Copied to clipboard",
) => {
  navigator.clipboard.writeText(content);
  toast.success(copyMessage);
};

export const getLogoURLByDomain = (domain?: string) => {
  return domain ? `https://logo.clearbit.com/${domain}` : "";
};

export const onCompanyLogoLoadError = (
  e: SyntheticEvent<HTMLImageElement, Event>,
) => {
  const target = e.target as HTMLImageElement;
  target.onerror = null;
  target.src = FALLBACK_IMAGES.ORGANIZATION_AVATAR;
};

export const onPersonImageLoadError = (
  e: SyntheticEvent<HTMLImageElement, Event>,
) => {
  const target = e.target as HTMLImageElement;
  target.onerror = null;
  target.src = FALLBACK_IMAGES.PERSON_AVATAR;
};

export function debounce(func: (...args: any[]) => void, wait: number) {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: any[]) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(later, wait);
  };
}

export const extractNameAndEmailFromEmailHeader = (emailHeader: string) => {
  if (!emailHeader) return { name: "", email: "" };

  let name = "";
  let email = "";

  if (emailHeader.includes("<")) {
    // Extracts name and email when both are present
    [name, email] = emailHeader.split("<");
    name = name.replace(/['"]/g, "").trim(); // Removes quotes and trims whitespace
  } else {
    // Handles case when only email is present
    email = emailHeader;
    name = email?.split("@")[0]; // Assumes the part before '@' is the name
  }

  email = email.replace(">", "").trim(); // Cleans the email string

  return {
    name,
    email,
  };
};

export const extractNamesAndEmailsFromEmailCCHeader = (emailHeader: string) => {
  if (!emailHeader) return [];
  const emails = emailHeader.split(",");
  return emails.map(email => extractNameAndEmailFromEmailHeader(email));
};

export const isKeyboardEvent = (event: any): event is React.KeyboardEvent => {
  return !!event.key;
};

export const getDomainFromEmail = (email: string) => {
  return email?.split("@")[1] || "";
};

export const checkIfEmailIsWorkEmail = (email: string) => {
  if (!email) return false;
  const EMAIL_PROVIDERS = ["gmail", "yahoo", "hotmail", "outlook", "icloud"];
  const domain = getDomainFromEmail(email);
  return !EMAIL_PROVIDERS.some(provider => domain.includes(provider));
};

/**
 *
 * @param text
 * @returns
 * @example
 * sanitizeCompanyDomainFromTextOrUrl('https://www.google.com') // google.com
 * sanitizeCompanyDomainFromTextOrUrl('google.com') // google.com
 * sanitizeCompanyDomainFromTextOrUrl('Google') // null
 * sanitizeCompanyDomainFromTextOrUrl('') // null
 * sanitizeCompanyDomainFromTextOrUrl('https://google.com') // google.com
 * sanitizeCompanyDomainFromTextOrUrl('https://www.google.com/search') // google.com
 */
export const sanitizeCompanyDomainFromTextOrUrl = (
  text: string | null | undefined,
  fallback?: string,
): string | null => {
  if (!text) return fallback || null;
  // Check if the text is a valid domain or URL
  const pattern = /^(https?:\/\/)?([a-z\d]([a-z\d-]*[a-z\d])*\.)+[a-z]{2,}$/i;
  if (!pattern.test(text)) return fallback || null;

  try {
    const url = new URL(text.startsWith("http") ? text : `http://${text}`);
    const hostname = url.hostname;
    const parts = hostname.split(".");

    // Adjust logic to handle subdomains
    // This example retains subdomains, but skips common prefixes like 'www'
    if (parts.length >= 3) {
      // Exclude common 'www' prefix but include other subdomains
      if (parts[0].toLowerCase() === "www") {
        return parts.slice(1).join(".");
      }
      return hostname; // Return the full hostname including subdomains
    }
    return hostname; // Directly return for domains without subdomains
  } catch {
    return fallback || null;
  }
};

export const extractTwoLettersFromName = (name?: string) => {
  if (!name) return "";

  const splitName = name.split(" ");
  const firstName = splitName[0];
  const lastName = splitName[splitName.length - 1];

  return lastName ? `${firstName[0]}${lastName[0]}` : firstName[0];
};

export const isValidDate = (value: string | null | undefined) =>
  value && new Date(value) instanceof Date && !isNaN(new Date(value).getTime());

export const getPlanName = (
  plan_type: "pro" | "free" | "basic" | "enterprise" | string | undefined,
) => {
  switch (plan_type) {
    case "pro":
      return "Pro";
    case "basic":
      return "Basic";
    case "enterprise":
      return "Enterprise";
    default:
      return "Free";
  }
};

export const ensureValidUrl = (urlString: string) => {
  if (typeof urlString !== "string" || !urlString.trim()) {
    return {
      isUrl: false,
      url: urlString || "",
    }; // Return false if urlString is not a valid string
  }

  const trimmedUrl = urlString.trim();
  const hasProtocol =
    trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://");
  const hasWWW = trimmedUrl.startsWith("www.");

  if (hasProtocol || hasWWW) {
    return {
      isUrl: true,
      url: trimmedUrl,
    }; // Return as is if it starts with http, https, or www
  }

  const pattern = new RegExp(
    "^(https?:\\/\\/)?" + // protocol is optional
      "(([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}" + // domain name
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
      "(\\?[;&amp;a-z\\d%_.~+=-]*)?" + // query string
      "(\\#[-a-z\\d_]*)?$",
    "i",
  ); // fragment locator

  if (pattern.test(trimmedUrl)) {
    return {
      isUrl: true,
      url: `https://${trimmedUrl}`,
    }; // Prepend https:// if it's a valid URL without http:// or https://
  }

  return {
    isUrl: false,
    url: trimmedUrl,
  }; // Return original if not a valid URL
};

export const formatNumber = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
  }).format(value);
};

export const getFirstLetterOfNameOrEmail = (data: {
  name?: string;
  email: string;
  uppercase?: boolean;
}) => {
  const { name, email, uppercase = true } = data;
  if (!name && !email) return null;
  const firstLetter = name?.trim().length ? name.charAt(0) : email.charAt(0);
  return uppercase ? firstLetter.toUpperCase() : firstLetter;
};

export const getTaskEntityLink = (entity: TaskEntityData) => {
  let link = "";
  switch (entity.object_type) {
    case TASK_ENTITY_TYPE.OPPORTUNITY:
      return (link = routes.opportunity.detailsPage(
        entity.object_id as string,
      ));
    case TASK_ENTITY_TYPE.ACCOUNT:
      return (link = routes.account.detailsPage(entity.object_id as string));
    case TASK_ENTITY_TYPE.CONTACT:
      return (link = routes.contact.detailsPage(entity.object_id as string));
    default:
      return link;
  }
};

export function formatLargeNumberWithSuffix(num: number) {
  const absNum = Math.abs(num);
  const sign = Math.sign(num);

  if (absNum >= 1e9) {
    return (sign * (absNum / 1e9)).toFixed(1) + "B";
  }
  if (absNum >= 1e6) {
    return (sign * (absNum / 1e6)).toFixed(1) + "M";
  }
  if (absNum >= 1e3) {
    return (sign * (absNum / 1e3)).toFixed(1) + "K";
  }
  return (sign * absNum).toString();
}

export const getFirstWordFromText = (text: string | null | undefined) => {
  if (!text) return "";

  let splittedText = text.split(" ");
  if (!splittedText.length) return "";

  let firstWord = splittedText[0];
  const isLongerThan7Chars = firstWord.length > 7;
  if (isLongerThan7Chars) {
    // Split by + or - or _ or @
    splittedText = firstWord.split(/[\+\-_@]/);
    firstWord = splittedText[0];
  }

  const firstWordWithFirstLetterCapitalized =
    firstWord.charAt(0).toUpperCase() + firstWord.slice(1);
  return firstWordWithFirstLetterCapitalized;
};

export const getPlainTextFromHTML = (html: string | null | undefined) => {
  return html?.replace(/<[^>]*>/g, "") || "";
};

/**
 * Returns the full name from first name and last name
 * @param firstName
 * @param lastName
 * @returns
 * @example
 * getFullnameFromFirstAndLastName('John', 'Doe') // John Doe
 */
export const getFullnameFromFirstAndLastName = (params: {
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  fallback?: string;
}) => {
  const { firstName, lastName, fallback } = params;

  return `${firstName || ""} ${lastName || ""}`.trim() || fallback || "";
};

export const getHtmlToMarkdown = (html: string) => {
  var turndownService = new TurndownService();
  turndownService.addRule("customBold", {
    filter: ["strong", "b"],
    replacement: function (content) {
      return "*" + content + "*";
    },
  });
  turndownService.addRule("customStrikethrough", {
    filter: ["del", "s"],
    replacement: function (content) {
      return "~" + content + "~";
    },
  });
  turndownService.addRule("customLink", {
    filter: "a",
    replacement: function (content, node) {
      const element = node as Element;
      return `<${element.getAttribute("href")}|${content}>`;
    },
  });

  return turndownService.turndown(html);
};

/**
 * Extracts the name from an email header part
 * @param emailHeaderPart
 * @returns
 * @example
 * extractNameFromEmail('John Doe <john.doe@example.com>') // { guessedFirstName: 'John', guessedLastName: 'Doe', guessedFullName: 'John Doe' }
 */
export const extractNameFromEmail = (emailHeaderPart: string) => {
  // Function to capitalize the first letter and lowercase the rest
  const capitalize = (s: string) =>
    s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

  // Extract the name and email parts from the header part
  const nameEmailMatch = emailHeaderPart.match(/(.*)<(.+@.+)>/);
  let localPart = "",
    guessedFullName = "";

  if (nameEmailMatch) {
    guessedFullName = nameEmailMatch[1].trim();
    localPart = nameEmailMatch?.[2]?.split("@")[0];
  } else {
    localPart = emailHeaderPart?.split("@")[0];
  }

  // Check if the local part contains separators for first and last names
  const hasSeparators =
    /\./.test(localPart) || /_/.test(localPart) || /-/.test(localPart);

  // If the full name is present, use it, otherwise check the local part for separators
  const names = guessedFullName
    ? guessedFullName.split(" ").map(capitalize)
    : hasSeparators
      ? localPart
          .replace(/\./g, " ")
          .replace(/_/g, " ")
          .replace(/-/g, " ")
          .split(" ")
          .map(capitalize)
      : [capitalize(localPart)];

  // Assign first name from the names array
  const guessedFirstName = names[0];

  // Assign last name if there are more parts after splitting, otherwise null
  const guessedLastName = names.length > 1 ? names.slice(1).join(" ") : null;

  return {
    guessedFirstName,
    guessedLastName,
    guessedFullName:
      guessedFullName ||
      (guessedLastName
        ? `${guessedFirstName} ${guessedLastName}`
        : guessedFirstName),
  };
};

/**
 * Validates if the provided string is a valid email address.
 *
 * @param {string} email - The email address to validate.
 * @returns {boolean} - Returns true if the email is valid, otherwise false.
 *
 * @example
 * isValidEmail('test@example.com'); // returns true
 * isValidEmail('invalid-email');    // returns false
 */
export const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const devLog = (...args: any[]) => {
  if (configuration.environment === "development") {
    console.log(...args);
  }
};

export const devError = (...args: any[]) => {
  if (configuration.environment === "development") {
    console.error(...args);
  }
};
